import { useState, useMemo } from "react";
import { showError, showSuccess } from "infra/helpers/alerta";
import { useFilters } from "core/features/filters/store";
import useMapData from "../../hooks/useMapData";
import useGetMatrizOperacoes from "./hooks/useGetMatrizOperacoes";
import useStore from "./hooks/useStore";
import ModalMatriz from "./components/ModalMatriz";
import ModalAcoes from "./components/ModalAcoes";
import useUpdateOrdemServico from "./hooks/useUpdateOrdemServico";
import { useQueryClient } from "react-query";
import URL from 'core/resources/urls';
import useFiltro from "../../hooks/useFiltro";

interface GestaoOperacoesLoteProps {
  action: "antecipar" | "prorrogar" | "justificar";
  planoOperacionalData: any;
  onClose: () => void;
}

function GestaoOperacoesLote({ action, planoOperacionalData, onClose }: GestaoOperacoesLoteProps) {

  const { filters } = useFilters();
  const { filtersAdvanced, unidade } = filters;
  const { classeOperacao } = filtersAdvanced;
  const queryClient = useQueryClient();
  const { filtroExecucaoOPeracional } = useFiltro();
  
  const idUnidade = unidade?.id;

  const { listTalhoesIds } = useMapData();
  const talhoesIds = useMemo(() => Array.from(listTalhoesIds), [listTalhoesIds]);
  const { data, isLoading } = useGetMatrizOperacoes(talhoesIds, classeOperacao);

  const [isModalMatrizOpen, setIsModalMatrizOpen] = useState(true);
  const [isModalAcoesOpen, setIsModalAcoesOpen] = useState(false);

  const { mutateAsync, isLoading: loadingUpdate } = useUpdateOrdemServico();

  const {
    motivo,
    observacao,
    reagendamentoQuantidadeDias,
    planoOperacionalOperacaoTalhaoIds,
  } = useStore();

  function getTipoAlteracao() {
    if (action === "antecipar") {
      return 1;
    }
    if (action === "prorrogar") {
      return 2;
    }
    return 4;
  }

  function getResultTitle() {
    if (action === "antecipar") {
      return "Operações antecipadas com sucesso";
    }
    if (action === "prorrogar") {
      return "Operações prorrogadas com sucesso";
    }
    return "Operações justificadas com sucesso";
  }

  async function handleUpdate() {
    try {
      const body = {
        motivo,
        observacao,
        reagendamentoQuantidadeDias,
        tipoAlteracao: getTipoAlteracao(),
        planoOperacionalOperacaoTalhaoIds,
      }
      await mutateAsync(body);
      const title = getResultTitle();
      queryClient.invalidateQueries([URL.PLANO_OPERACIONAL.OBTER_MAPA, idUnidade, filtroExecucaoOPeracional]);
      showSuccess(title);
      handleClose();
    } catch (error: any) {
      if (error.response) {
        const message = error.response?.data?.Details?.[0]?.Errors?.[0];
        showError(message ?? "Erro ao atualizar a operação");
        return;
      }
      showError("Erro ao atualizar a operação");
    }
  }

  function handleOpenModalAcoes() {
    setIsModalMatrizOpen(false);
    setIsModalAcoesOpen(true);
  }

  function handleClose() {
    setIsModalMatrizOpen(false);
    setIsModalAcoesOpen(false);
    onClose();
  }

  function isBlocked(operacao: any, index: number, isNewGroup: boolean, talhaoAnterior: any) {
    if (action === "justificar") {
      if (operacao.status === "Atrasada" && !operacao.possuiJustificativaAtraso) {
        return false;
      }
      return true;
    }
    if (action === "antecipar" || action === "prorrogar") {
      if (operacao.status === "Planejada") {
        return false;
      }
      return true;
    }
    if (talhaoAnterior?.areaDisponivelPorcentagem <= 0 && !isNewGroup) {
      return false;
    }
    if (operacao.areaDisponivelPorcentagem <= 0) {
      return true;
    }
    if (index === 0 && operacao.status !== "Executada") {
      return false;
    }
    if (index !== 0 && isNewGroup && operacao.status !== "Executada") {
      return false;
    }
    if (talhaoAnterior && talhaoAnterior?.status !== "Executada" && talhaoAnterior?.status !== "ExecutadaParcialmente") {
      return true;
    }
    if (operacao.status === "Executada") {
      return true;
    }
    if (!operacao.operacaoClasse) {
      return true;
    }
    return false;
  }

  function isNewGroup(operacao: string, index: number, arr: any[]): boolean {
    if (index === 0) return false;
    return operacao !== arr[index - 1].operacaoClasse;
  }

  const filteredResult = useMemo(() => {
    if (!data) return [];

    return data.map((operacao: any) => {
      const listaFiltrada = operacao?.planoOperacionalOperacaoTalhao
        ?.sort((a: any, b: any) => {
          const classeCompare = a.operacaoClasse.localeCompare(b.operacaoClasse);
          if (classeCompare !== 0) return classeCompare;
          return a.dataProgramada.localeCompare(b.dataProgramada);
        })
        ?.map((talhao: any, index: number, arr: any[]) => {
          const talhaoAnterior = arr[index - 1];
          const newGroup = isNewGroup(talhao.operacaoClasse, index, arr);

          return {
            ...talhao,
            bloqueado: isBlocked(talhao, index, newGroup, talhaoAnterior),
          }
        });

      return {
        ...operacao,
        planoOperacionalOperacaoTalhao: listaFiltrada,
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function contagemClasses(talhoes: any[]) {
    const contagem: Record<string, number> = {};

    const operacoes = talhoes.flatMap(t => t.planoOperacionalOperacaoTalhao);
    const classes = Array.from(new Set(operacoes.map(o => o.operacaoClasse)));

    classes.forEach(classe => {
      contagem[classe] = 0;
    });

    talhoes.forEach(talhao => {
      const operacoesTalhao: any[] = talhao.planoOperacionalOperacaoTalhao;

      classes.forEach(classe => {
        const operacoesPorClasse = operacoesTalhao.filter(o => o.operacaoClasse === classe);
        const quantidade = operacoesPorClasse.length;
        if (contagem[classe] < quantidade) {
          contagem[classe] = quantidade;
        }
      });
    });

    return contagem;
  }

  function completeOperacoesTalhoes() {
    const contagem = contagemClasses(filteredResult);
    const classes = Object.keys(contagem);

    return filteredResult.map((talhao: any) => {
      const operacoes = [...talhao.planoOperacionalOperacaoTalhao];
      const contagemTalhao: Record<string, number> = {};

      operacoes.forEach(op => {
        contagemTalhao[op.operacaoClasse] = (contagemTalhao[op.operacaoClasse] || 0) + 1;
      });

      classes.forEach(classe => {
        const lack = (contagem[classe] || 0) - (contagemTalhao[classe] || 0);

        for (let i = 0; i < lack; i++) {
          operacoes.push({
            isPlaceholder: true,
            operacaoClasse: classe
          });
        }
      });

      operacoes.sort((a, b) => a.operacaoClasse.localeCompare(b.operacaoClasse));

      const sorted = [...operacoes].sort((a: any, b: any) => {
          const getDataOrdenacao = (item: any) => {
            const dataRef =
              item?.periodoDataInicio && item?.periodoDataFim
                ? item.periodoDataInicio
                : item?.dataProgramada;

            return dataRef ? new Date(dataRef).getTime() : Infinity;
          };

          return getDataOrdenacao(a) - getDataOrdenacao(b);
        });

      sorted.forEach((item, index: number) => {
        item.index = index + 1;
      });

      return {
        ...talhao,
        planoOperacionalOperacaoTalhao: sorted,
      }
    });
  }

  const orderedResult = completeOperacoesTalhoes();

  return (
    <>
      <ModalMatriz
        data={orderedResult}
        planoOperacionalData={planoOperacionalData}
        talhoesIds={talhoesIds}
        action={action}
        isLoading={isLoading}
        isOpen={isModalMatrizOpen}
        onClose={handleClose}
        next={handleOpenModalAcoes}
      />

      <ModalAcoes
        action={action}
        data={orderedResult}
        planoOperacionalData={planoOperacionalData}
        isLoading={isLoading || loadingUpdate}
        isOpen={isModalAcoesOpen}
        onClose={handleClose}
        next={handleUpdate}
      />
    </>
  );
}

export default GestaoOperacoesLote;
